import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { ContexteA11y } from './contexte';
import {
  EXCLUSIONS,
  OPTIONS_ACCESSIBILITE,
  type CleOption,
  type EtatAccessibilite,
} from './options';

/** Clé de conservation des préférences dans le navigateur. */
const STOCKAGE = 'topformation.accessibilite';

/**
 * Adaptations d'accessibilité, appliquées à toute l'application (CDC §9).
 *
 * Les préférences sont conservées **dans le navigateur de la personne**, et
 * non en base : ce sont des réglages de confort de lecture, propres à un poste
 * et à un moment, et l'on ne veut pas que le choix d'un mode dyslexie fasse
 * une donnée de plus dans un dossier apprenant.
 *
 * La lecture est défensive de bout en bout : un navigateur en navigation
 * privée, un stockage désactivé ou une synthèse vocale absente ne doivent pas
 * empêcher l'application de s'afficher.
 *
 * `actif` : le compte a l'écran « Accessibilité » (apprenant, formateur, pôle
 * pédagogique, TF-491). Sans lui, aucune adaptation ne s'applique : celles que
 * ce poste a gardées, d'un apprenant peut-être, resteraient sinon actives
 * sans bouton pour les couper. Elles restent conservées pour qui les a.
 */
export function FournisseurAccessibilite({
  actif,
  children,
}: {
  actif: boolean;
  children: ReactNode;
}): JSX.Element {
  const [etat, setEtat] = useState<EtatAccessibilite>(() => lireStockage());
  const [panneauOuvert, setPanneauOuvert] = useState(false);

  const lectureVocaleDisponible =
    typeof window !== 'undefined' && 'speechSynthesis' in window;

  // Les classes vivent sur `<body>` : les styles de la maquette y sont accrochés.
  useEffect(() => {
    for (const option of OPTIONS_ACCESSIBILITE) {
      if (option.cle === 'tts') continue;
      document.body.classList.toggle(option.cle, actif && etat[option.cle] === true);
    }
    ecrireStockage(etat);
  }, [etat, actif]);

  // Règle de lecture : une bande qui suit le pointeur. L'écouteur n'est posé
  // que lorsque l'option est active, pour ne pas suivre la souris pour rien.
  useEffect(() => {
    if (!actif || !etat['a11y-ruler']) return;

    const suivre = (evenement: MouseEvent): void => {
      document.documentElement.style.setProperty(
        '--a11y-regle',
        `${Math.round(evenement.clientY - 21)}px`,
      );
    };
    document.addEventListener('mousemove', suivre);
    return () => document.removeEventListener('mousemove', suivre);
  }, [etat, actif]);

  // Lecture vocale : la synthèse du navigateur, sans aucun service externe.
  // Aucun prestataire n'est retenu sur ce projet, et il n'en faut pas ici.
  useEffect(() => {
    if (!actif || !etat['tts'] || !lectureVocaleDisponible) return;

    const lireSelection = (): void => {
      const texte = window.getSelection()?.toString().trim();
      if (!texte) return;
      window.speechSynthesis.cancel();
      const enonce = new SpeechSynthesisUtterance(texte.slice(0, 400));
      enonce.lang = 'fr-FR';
      window.speechSynthesis.speak(enonce);
    };
    // Au clavier, la sélection se fait en tenant Maj : elle est lue quand la
    // touche est relâchée, comme au relâcher du bouton de la souris.
    const auRelacherMaj = (evenement: KeyboardEvent): void => {
      if (evenement.key === 'Shift') lireSelection();
    };

    document.addEventListener('mouseup', lireSelection);
    document.addEventListener('keyup', auRelacherMaj);
    return () => {
      document.removeEventListener('mouseup', lireSelection);
      document.removeEventListener('keyup', auRelacherMaj);
      window.speechSynthesis.cancel();
    };
  }, [etat, actif, lectureVocaleDisponible]);

  const basculer = useCallback((cle: CleOption) => {
    setEtat((precedent) => {
      const actif = precedent[cle] !== true;
      const suivant: EtatAccessibilite = { ...precedent, [cle]: actif };
      // Une option exclusive éteint sa jumelle : deux tailles de texte
      // simultanées donneraient un résultat imprévisible.
      const exclue = EXCLUSIONS[cle];
      if (actif && exclue) suivant[exclue] = false;
      return suivant;
    });
  }, []);

  const reinitialiser = useCallback(() => setEtat({}), []);
  const ouvrirPanneau = useCallback(() => setPanneauOuvert(true), []);
  const fermerPanneau = useCallback(() => setPanneauOuvert(false), []);

  const valeur = useMemo(
    () => ({
      etat,
      basculer,
      reinitialiser,
      nombreActives: Object.values(etat).filter(Boolean).length,
      lectureVocaleDisponible,
      panneauOuvert,
      ouvrirPanneau,
      fermerPanneau,
    }),
    [etat, basculer, reinitialiser, lectureVocaleDisponible, panneauOuvert, ouvrirPanneau, fermerPanneau],
  );

  return <ContexteA11y.Provider value={valeur}>{children}</ContexteA11y.Provider>;
}

function lireStockage(): EtatAccessibilite {
  try {
    const brut = window.localStorage.getItem(STOCKAGE);
    if (!brut) return {};
    const analyse = JSON.parse(brut) as unknown;
    if (typeof analyse !== 'object' || analyse === null) return {};

    // Seules les clés connues sont retenues : une préférence enregistrée par
    // une version antérieure ne doit pas poser une classe qui n'existe plus.
    const connues = new Set<string>(OPTIONS_ACCESSIBILITE.map((option) => option.cle));
    const etat: EtatAccessibilite = {};
    for (const [cle, valeur] of Object.entries(analyse)) {
      if (connues.has(cle) && valeur === true) etat[cle as CleOption] = true;
    }
    return etat;
  } catch {
    // Navigation privée, stockage refusé, JSON abîmé : on repart des réglages
    // par défaut plutôt que d'empêcher l'application de s'afficher.
    return {};
  }
}

function ecrireStockage(etat: EtatAccessibilite): void {
  try {
    window.localStorage.setItem(STOCKAGE, JSON.stringify(etat));
  } catch {
    // Le réglage reste actif pour la session en cours, sans être conservé.
  }
}
